import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { AppShell } from "../components/layout/AppShell";
import { GPTIcon, AVAILABLE_ICONS } from "../components/GPTIcon";
import { useAuth, isMasterAdmin } from "../context/AuthContext";
import {
  Lock,
  Unlock,
  Save,
  Loader2,
  Users,
  MessageSquare,
  Activity,
  Database,
  KeyRound,
  ShieldCheck,
  ShieldOff,
  CheckCircle2,
  Crown,
  GraduationCap,
} from "lucide-react";
import { toast, Toaster } from "sonner";

export default function Admin() {
  const { user: currentUser } = useAuth();
  const iAmMasterAdmin = isMasterAdmin(currentUser?.role);
  const [gpts, setGpts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // full gpt being edited
  const [saving, setSaving] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // user being edited (access drawer)
  const [resettingFor, setResettingFor] = useState(null); // user we're resetting password for
  const [newPassword, setNewPassword] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [gptRes, anRes, usersRes] = await Promise.all([
        api.get("/admin/gpts"),
        api.get("/admin/analytics"),
        api.get("/admin/users"),
      ]);
      setGpts(gptRes.data);
      setAnalytics(anRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleLock = async (gpt) => {
    try {
      const { data } = await api.patch(`/admin/gpts/${gpt.id}`, {
        locked: !gpt.locked,
      });
      setGpts((prev) => prev.map((g) => (g.id === gpt.id ? data : g)));
      toast.success(`${data.name} ${data.locked ? "locked" : "unlocked"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
  };

  const saveEditing = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/gpts/${editing.id}`, {
        name: editing.name,
        description: editing.description,
        icon: editing.icon,
        system_prompt: editing.system_prompt,
        provider: editing.provider,
        conversation_starter: editing.conversation_starter ?? null,
        access_type: editing.access_type || "Free",
      });
      setGpts((prev) => prev.map((g) => (g.id === editing.id ? data : g)));
      toast.success("Saved");
      setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const updateUser = async (userId, patch) => {
    try {
      const { data } = await api.patch(`/admin/users/${userId}`, patch);
      setUsers((prev) => prev.map((u) => (u.id === userId ? data : u)));
      if (editingUser?.id === userId) setEditingUser(data);
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
      throw err;
    }
  };

  const toggleActive = async (u) => {
    await updateUser(u.id, { is_active: !u.is_active });
    toast.success(`${u.email} ${!u.is_active ? "activated" : "deactivated"}`);
  };

  const toggleMentorship = async (u) => {
    const next = !u.has_mentorship_access;
    await updateUser(u.id, { has_mentorship_access: next });
    toast.success(
      next
        ? `6 Months Mentorship unlocked for ${u.email}`
        : `6 Months Mentorship revoked for ${u.email}`,
    );
  };

  const toggleAdminRole = async (u) => {
    // Only the master admin can promote/demote — the button is only visible
    // to master admin, but we double-guard on the client too.
    if (!iAmMasterAdmin) {
      toast.error("Only the master admin can change roles");
      return;
    }
    if (u.role === "master_admin") {
      toast.error("The master admin role cannot be changed");
      return;
    }
    const nextRole = u.role === "admin" ? "user" : "admin";
    try {
      await updateUser(u.id, { role: nextRole });
      toast.success(
        nextRole === "admin"
          ? `${u.email} is now an admin`
          : `${u.email} demoted to user`,
      );
    } catch {
      /* toast handled inside updateUser */
    }
  };

  const submitPasswordReset = async () => {
    if (!resettingFor) return;
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      const { data } = await api.post(`/admin/users/${resettingFor.id}/reset-password`, {
        new_password: newPassword,
      });
      toast.success(data?.message || "Password reset");
      setResettingFor(null);
      setNewPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password reset failed");
    }
  };

  return (
    <AppShell>
      <Toaster position="top-center" richColors />
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-8 md:py-12">
        <header className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              Admin Console
            </div>
            <h1
              data-testid="admin-title"
              className="mt-1 font-heading text-3xl md:text-5xl font-bold tracking-tighter"
            >
              System Control
            </h1>
            <p className="text-muted-foreground text-sm md:text-base mt-2 max-w-xl">
              Manage GPTs, edit system prompts, and monitor usage.
            </p>
          </div>
        </header>

        {/* Analytics */}
        <section className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analytics-cards">
          <StatCard icon={Users} label="Total users" value={analytics?.total_users ?? "—"} testId="stat-users" />
          <StatCard icon={Activity} label="Active (7d)" value={analytics?.active_users_7d ?? "—"} testId="stat-active" />
          <StatCard icon={MessageSquare} label="Sessions" value={analytics?.total_sessions ?? "—"} testId="stat-sessions" />
          <StatCard icon={Database} label="Messages" value={analytics?.total_messages ?? "—"} testId="stat-messages" />
        </section>

        {/* Per-GPT analytics */}
        {analytics?.per_gpt?.length > 0 && (
          <section className="mt-8 bg-card border border-border/60 rounded-[24px] overflow-hidden shadow-subtle">
            <div className="px-6 py-4 border-b border-border/60 flex items-center justify-between">
              <div className="font-heading text-lg tracking-tight">Usage by GPT</div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                Live
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="usage-table">
                <thead className="text-left">
                  <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-6 py-3 font-medium">GPT</th>
                    <th className="px-6 py-3 font-medium">Provider</th>
                    <th className="px-6 py-3 font-medium">Status</th>
                    <th className="px-6 py-3 font-medium text-right">Sessions</th>
                    <th className="px-6 py-3 font-medium text-right">Messages</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.per_gpt.map((row) => (
                    <tr key={row.gpt_id} className="border-t border-border/60">
                      <td className="px-6 py-3 font-medium">{row.name}</td>
                      <td className="px-6 py-3 text-muted-foreground capitalize">
                        {row.provider === "anthropic" ? "Reasoning cluster" : "Strategy cluster"}
                      </td>
                      <td className="px-6 py-3">
                        <span
                          className={[
                            "px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold border",
                            row.locked
                              ? "bg-destructive/10 text-destructive border-destructive/30"
                              : "bg-foreground/5 border-border/60 text-foreground/80",
                          ].join(" ")}
                        >
                          {row.locked ? "Locked" : "Available"}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-right tabular-nums">{row.sessions}</td>
                      <td className="px-6 py-3 text-right tabular-nums">{row.messages}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* GPT management */}
        <section className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl tracking-tight">GPT Management</h2>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-20 rounded-2xl bg-secondary/60 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-3" data-testid="gpt-admin-list">
              {gpts.map((g) => (
                <div
                  key={g.id}
                  className="bg-card border border-border/60 rounded-2xl p-4 md:p-5 flex items-center gap-4 shadow-subtle"
                >
                  <div className="h-11 w-11 rounded-xl bg-secondary border border-border/60 flex items-center justify-center shrink-0">
                    <GPTIcon name={g.icon} size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{g.name}</span>
                      <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                        {g.provider === "anthropic" ? "Reasoning" : "Strategy"}
                      </span>
                      {g.locked && (
                        <span className="text-[10px] uppercase tracking-widest font-bold text-destructive">
                          Locked
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {g.description}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`toggle-lock-${g.slug}`}
                      onClick={() => toggleLock(g)}
                      className={[
                        "h-9 px-3 rounded-full border text-xs font-medium inline-flex items-center gap-1.5 transition-colors",
                        g.locked
                          ? "bg-destructive text-destructive-foreground border-destructive"
                          : "border-border/70 hover:bg-secondary",
                      ].join(" ")}
                    >
                      {g.locked ? <Lock size={13} /> : <Unlock size={13} />}
                      {g.locked ? "Unlock" : "Lock"}
                    </button>
                    <button
                      data-testid={`edit-gpt-${g.slug}`}
                      onClick={() => setEditing({ ...g })}
                      className="h-9 px-4 rounded-full bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 transition"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* User Management */}
        <section className="mt-12" data-testid="user-mgmt-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl tracking-tight">User Management</h2>
            <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              {users.length} total
            </span>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-20 rounded-2xl bg-secondary/60 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-3" data-testid="user-admin-list">
              {users.map((u) => {
                const accessLabel =
                  u.allowed_gpts === null || u.allowed_gpts === undefined
                    ? `All ${gpts.length} GPTs`
                    : u.allowed_gpts.length === 0
                    ? "No GPTs"
                    : `${u.allowed_gpts.length} of ${gpts.length} GPTs`;
                const isMaster = u.role === "master_admin";
                const isAdminUser = u.role === "admin";
                const hasMentorship = !!u.has_mentorship_access;
                return (
                  <div
                    key={u.id}
                    data-testid={`user-row-${u.id}`}
                    className="bg-card border border-border/60 rounded-2xl p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4 shadow-subtle"
                  >
                    <div className="h-11 w-11 rounded-xl bg-secondary border border-border/60 flex items-center justify-center shrink-0 text-sm font-semibold uppercase">
                      {(u.name || u.email).charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{u.name || u.email}</span>
                        <span
                          data-testid={`user-role-badge-${u.id}`}
                          className={[
                            "text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border inline-flex items-center gap-1",
                            isMaster
                              ? "border-[#b89846] text-black bg-gradient-to-r from-[#e1c074] to-[#8a6f2a]"
                              : isAdminUser
                              ? "border-[#b89846]/60 text-[#e8c879] bg-[#b89846]/10"
                              : "border-border/70 text-muted-foreground",
                          ].join(" ")}
                        >
                          {isMaster && <Crown size={10} strokeWidth={2.2} />}
                          {isMaster ? "master admin" : u.role}
                        </span>
                        {hasMentorship && !isMaster && !isAdminUser && (
                          <span
                            data-testid={`user-mentorship-badge-${u.id}`}
                            className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border border-[#b89846]/60 text-[#e8c879] bg-[#b89846]/10 inline-flex items-center gap-1"
                          >
                            <GraduationCap size={10} strokeWidth={2.2} /> 6 months
                          </span>
                        )}
                        {!u.is_active && (
                          <span className="text-[10px] uppercase tracking-widest font-bold text-destructive">
                            Deactivated
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {u.email} · {accessLabel}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Mentorship toggle — hidden for admins/master admins
                          who already have full mentorship access implicitly. */}
                      {!isMaster && !isAdminUser && (
                        <button
                          data-testid={`user-toggle-mentorship-${u.id}`}
                          onClick={() => toggleMentorship(u)}
                          className={[
                            "h-9 px-3 rounded-full border text-xs font-medium inline-flex items-center gap-1.5 transition-colors",
                            hasMentorship
                              ? "bg-[#b89846] text-black border-[#b89846] hover:opacity-90"
                              : "border-border/70 hover:bg-secondary",
                          ].join(" ")}
                          title={
                            hasMentorship
                              ? "Revoke 6 Months Mentorship access"
                              : "Grant 6 Months Mentorship access"
                          }
                        >
                          <GraduationCap size={13} />
                          {hasMentorship ? "Revoke 6 months" : "Grant 6 months"}
                        </button>
                      )}
                      {/* Promote/Demote admin — master-admin only, and never
                          for the master admin themselves. */}
                      {iAmMasterAdmin && !isMaster && (
                        <button
                          data-testid={`user-toggle-admin-${u.id}`}
                          onClick={() => toggleAdminRole(u)}
                          className={[
                            "h-9 px-3 rounded-full border text-xs font-medium inline-flex items-center gap-1.5 transition-colors",
                            isAdminUser
                              ? "border-border/70 hover:bg-secondary"
                              : "bg-[#b89846] text-black border-[#b89846] hover:opacity-90",
                          ].join(" ")}
                          title={
                            isAdminUser
                              ? "Demote to regular user"
                              : "Promote to admin"
                          }
                        >
                          <ShieldCheck size={13} />
                          {isAdminUser ? "Demote admin" : "Make admin"}
                        </button>
                      )}
                      <button
                        data-testid={`user-toggle-active-${u.id}`}
                        onClick={() => toggleActive(u)}
                        className={[
                          "h-9 px-3 rounded-full border text-xs font-medium inline-flex items-center gap-1.5 transition-colors",
                          u.is_active
                            ? "border-border/70 hover:bg-secondary"
                            : "bg-destructive text-destructive-foreground border-destructive",
                        ].join(" ")}
                      >
                        {u.is_active ? (
                          <>
                            <ShieldOff size={13} /> Revoke
                          </>
                        ) : (
                          <>
                            <ShieldCheck size={13} /> Grant
                          </>
                        )}
                      </button>
                      <button
                        data-testid={`user-edit-access-${u.id}`}
                        onClick={() => setEditingUser({ ...u, allowed_gpts: u.allowed_gpts ?? null })}
                        className="h-9 px-3 rounded-full border border-border/70 text-xs font-medium hover:bg-secondary transition inline-flex items-center gap-1.5"
                      >
                        <Users size={13} /> GPT access
                      </button>
                      <button
                        data-testid={`user-reset-password-${u.id}`}
                        onClick={() => {
                          setResettingFor(u);
                          setNewPassword("");
                        }}
                        className="h-9 px-3 rounded-full bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 transition inline-flex items-center gap-1.5"
                      >
                        <KeyRound size={13} /> Reset password
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* GPT Access drawer for a user */}
      {editingUser && (
        <UserAccessDrawer
          user={editingUser}
          gpts={gpts}
          onClose={() => setEditingUser(null)}
          onSave={async (patch) => {
            await updateUser(editingUser.id, patch);
            toast.success("Access updated");
            setEditingUser(null);
          }}
        />
      )}

      {/* Reset-password modal */}
      {resettingFor && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/20 backdrop-blur-sm"
          onClick={() => {
            setResettingFor(null);
            setNewPassword("");
          }}
          data-testid="reset-password-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-card border border-border/60 rounded-3xl p-6 shadow-hover"
          >
            <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              Reset password
            </div>
            <h3 className="font-heading text-2xl tracking-tight mt-1">
              {resettingFor.name || resettingFor.email}
            </h3>
            <p className="text-sm text-muted-foreground mt-2">{resettingFor.email}</p>

            <label className="block mt-5">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                New password (min 6 chars)
              </span>
              <input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="reset-password-input"
                className="mt-1.5 w-full h-11 rounded-xl bg-background border border-border px-4 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#b89846]/40"
                autoFocus
              />
            </label>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => {
                  setResettingFor(null);
                  setNewPassword("");
                }}
                className="h-11 px-5 rounded-full border border-border/60 text-sm hover:bg-secondary transition"
              >
                Cancel
              </button>
              <button
                onClick={submitPasswordReset}
                data-testid="reset-password-confirm"
                className="h-11 px-5 rounded-full bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2 hover:opacity-90 transition"
              >
                <KeyRound size={14} /> Reset password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit drawer */}
      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-stretch md:items-center justify-center p-0 md:p-6 bg-foreground/20 backdrop-blur-sm"
          onClick={() => setEditing(null)}
          data-testid="edit-drawer"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl bg-card border border-border/60 rounded-t-[24px] md:rounded-[24px] shadow-hover p-6 md:p-8 max-h-[96vh] overflow-y-auto thin-scroll"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  Edit GPT
                </div>
                <h3 className="font-heading text-2xl tracking-tight mt-1">{editing.name}</h3>
              </div>
              <button
                onClick={() => setEditing(null)}
                className="h-9 w-9 rounded-full border border-border/60 hover:bg-secondary transition-colors"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="mt-6 space-y-4">
              <Field label="Name">
                <input
                  data-testid="edit-name"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full h-11 rounded-xl bg-background border border-border px-4 text-[15px] focus:outline-none focus:ring-2 focus:ring-foreground/10"
                />
              </Field>
              <Field label="Description">
                <input
                  data-testid="edit-description"
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="w-full h-11 rounded-xl bg-background border border-border px-4 text-[15px] focus:outline-none focus:ring-2 focus:ring-foreground/10"
                />
              </Field>
              <Field label="Icon">
                <div className="flex flex-wrap gap-2" data-testid="icon-picker">
                  {AVAILABLE_ICONS.map((ic) => (
                    <button
                      key={ic}
                      onClick={() => setEditing({ ...editing, icon: ic })}
                      className={[
                        "h-10 w-10 rounded-xl border flex items-center justify-center transition",
                        editing.icon === ic
                          ? "border-foreground bg-secondary"
                          : "border-border/60 hover:bg-secondary/60",
                      ].join(" ")}
                      type="button"
                    >
                      <GPTIcon name={ic} size={18} />
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Provider (internal routing)">
                <div className="flex gap-2" data-testid="provider-picker">
                  {[
                    { key: "openai", label: "Strategy cluster" },
                    { key: "anthropic", label: "Reasoning cluster" },
                  ].map((p) => (
                    <button
                      key={p.key}
                      onClick={() => setEditing({ ...editing, provider: p.key })}
                      type="button"
                      className={[
                        "h-10 px-4 rounded-full text-sm border transition-colors",
                        editing.provider === p.key
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border/60 hover:bg-secondary",
                      ].join(" ")}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Access type">
                <div className="flex gap-2" data-testid="access-type-picker">
                  {["Free", "Pro"].map((p) => (
                    <button
                      key={p}
                      onClick={() => setEditing({ ...editing, access_type: p })}
                      type="button"
                      className={[
                        "h-10 px-4 rounded-full text-sm border transition-colors",
                        (editing.access_type || "Free") === p
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border/60 hover:bg-secondary",
                      ].join(" ")}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Conversation starter (button shown on empty chat)">
                <input
                  data-testid="edit-conversation-starter"
                  value={editing.conversation_starter || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, conversation_starter: e.target.value })
                  }
                  placeholder="e.g. Let's Go"
                  className="w-full h-11 rounded-xl bg-background border border-border px-4 text-[15px] focus:outline-none focus:ring-2 focus:ring-foreground/10"
                />
              </Field>
              <Field label="System prompt">
                <textarea
                  data-testid="edit-system-prompt"
                  value={editing.system_prompt}
                  onChange={(e) => setEditing({ ...editing, system_prompt: e.target.value })}
                  rows={10}
                  className="w-full rounded-xl bg-background border border-border px-4 py-3 text-sm font-mono leading-relaxed thin-scroll focus:outline-none focus:ring-2 focus:ring-foreground/10"
                />
              </Field>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                className="h-11 px-5 rounded-full border border-border/60 text-sm hover:bg-secondary transition"
              >
                Cancel
              </button>
              <button
                data-testid="edit-save"
                onClick={saveEditing}
                disabled={saving}
                className="h-11 px-5 rounded-full bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2 hover:opacity-90 transition disabled:opacity-60"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div
      data-testid={testId}
      className="relative bg-card border border-border/60 rounded-2xl p-5 shadow-subtle overflow-hidden"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <Icon size={16} strokeWidth={1.6} className="text-muted-foreground" />
      </div>
      <div className="font-heading text-3xl md:text-4xl tracking-tighter font-semibold mt-3 tabular-nums">
        {value}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

/**
 * UserAccessDrawer — admin tool to grant/revoke per-user GPT access.
 *
 * Access model:
 *  - `allowed_gpts === null`  → user can see ALL GPTs (default)
 *  - `allowed_gpts === []`    → user can see NONE
 *  - `allowed_gpts === [...]` → user can see only the listed GPT slugs
 *
 * The drawer lets the admin pick "All", "None", or a custom subset.
 */
function UserAccessDrawer({ user, gpts, onClose, onSave }) {
  const initialMode =
    user.allowed_gpts === null || user.allowed_gpts === undefined
      ? "all"
      : user.allowed_gpts.length === 0
      ? "none"
      : "custom";
  const [mode, setMode] = useState(initialMode);
  const [selected, setSelected] = useState(
    new Set(user.allowed_gpts && user.allowed_gpts.length ? user.allowed_gpts : []),
  );
  const [saving, setSaving] = useState(false);

  const toggleSlug = (slug) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const handleSave = async () => {
    let allowed_gpts;
    if (mode === "all") allowed_gpts = null;
    else if (mode === "none") allowed_gpts = [];
    else allowed_gpts = Array.from(selected);
    setSaving(true);
    try {
      await onSave({ allowed_gpts });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-foreground/20 backdrop-blur-sm"
      onClick={onClose}
      data-testid="user-access-drawer"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md h-full bg-card border-l border-border/60 overflow-y-auto"
      >
        <div className="p-6 border-b border-border/60 sticky top-0 bg-card z-10">
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            Manage GPT access
          </div>
          <h3 className="font-heading text-2xl tracking-tight mt-1">
            {user.name || user.email}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">{user.email}</p>
        </div>

        <div className="p-6 space-y-5">
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Access mode
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: "all", label: "All GPTs" },
                { key: "custom", label: "Custom" },
                { key: "none", label: "No access" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setMode(opt.key)}
                  data-testid={`access-mode-${opt.key}`}
                  className={[
                    "h-10 rounded-full text-xs uppercase tracking-wider font-bold border-[1.5px] transition",
                    mode === opt.key
                      ? "bg-[#b89846] text-black border-[#b89846]"
                      : "border-border/70 hover:bg-secondary",
                  ].join(" ")}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {mode === "custom" && (
            <div className="space-y-2" data-testid="gpt-checklist">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">
                Pick GPTs ({selected.size} selected)
              </div>
              <div className="space-y-2">
                {gpts.map((g) => (
                  <label
                    key={g.id}
                    className="flex items-center gap-3 p-3 rounded-xl border border-border/60 hover:bg-secondary/60 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(g.slug)}
                      onChange={() => toggleSlug(g.slug)}
                      data-testid={`gpt-check-${g.slug}`}
                      className="h-4 w-4 accent-[#b89846]"
                    />
                    <div className="h-9 w-9 rounded-lg bg-secondary border border-border/60 flex items-center justify-center">
                      <GPTIcon name={g.icon} size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{g.name}</div>
                      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                        {g.category === "mentorship" ? "Mentorship" : "Action Program"}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-border/60">
            <button
              onClick={onClose}
              className="h-11 px-5 rounded-full border border-border/60 text-sm hover:bg-secondary transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="user-access-save"
              className="h-11 px-5 rounded-full bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2 hover:opacity-90 disabled:opacity-60 transition"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Save access
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
